// Pure client-side windowing for the portfolio TWR curve.
//
// The backend (`PortfolioTwrView` → `twr_curve.portfolio_twr`) returns the FULL
// inception→today daily series as `[{ date:'YYYY-MM-DD', cumulative:Number }, …]`,
// where `cumulative` is the cumulative return since inception (0.084 = +8.4%).
// TWR is chain-linked, so ANY window is a rebase of that one series — no backend
// call per range:
//
//     windowValue(t) = (1 + cum[t]) / (1 + cum[anchor]) - 1
//
// The anchor snaps to the last series point on/before the window's calendar floor
// (Jan-1 / month-1 aren't trading days), i.e. the prior close. `spy_series` and
// `old_series` share the same daily grid and rebase identically.
//
// This module is deliberately pure (no React/DOM) so it is unit-testable.

export const RANGES = ['MTD', '1M', '3M', 'YTD', '1Y', 'ALL'];
export const RANGE_LABELS = { MTD: 'MTD', '1M': '1M', '3M': '3M', YTD: 'YTD', '1Y': '1Y', ALL: 'All' };

// UTC-safe ISO (YYYY-MM-DD) shift helpers — string dates compare lexically since
// they're zero-padded ISO, so we never need Date parsing for the anchor scan.
function isoShift(iso, { months = 0, years = 0 } = {}) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (years) d.setUTCFullYear(d.getUTCFullYear() + years);
  if (months) d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

// Calendar floor for a range, given the as-of date (the series' last date).
// The anchor is the last series point with date <= this floor.
export function windowFloor(range, asOf) {
  const y = asOf.slice(0, 4);
  const m = asOf.slice(0, 7);
  switch (range) {
    case 'MTD': return `${m}-01`;
    case 'YTD': return `${y}-01-01`;
    case '1M': return isoShift(asOf, { months: -1 });
    case '3M': return isoShift(asOf, { months: -3 });
    case '1Y': return isoShift(asOf, { years: -1 });
    case 'ALL':
    default: return '0000-01-01';
  }
}

// Index of the anchor point: the last point whose date <= floor. If every point
// is after the floor (window older than inception), anchor at index 0 (inception).
// `series` is assumed ascending by date.
export function anchorIndex(series, floor) {
  let idx = 0;
  for (let i = 0; i < series.length; i += 1) {
    if (series[i].date <= floor) idx = i; else break;
  }
  return idx;
}

// Rebase a cumulative-return series to a window. Returns:
//   { points: [{date, value}], windowReturn, anchorDate }
// where `points` are rebased so the anchor reads 0% and `windowReturn` is the
// window's total return (last point's value), or null if the series is empty.
export function rebaseSeries(series, range, asOf) {
  if (!Array.isArray(series) || series.length === 0) {
    return { points: [], windowReturn: null, anchorDate: null };
  }
  const stamp = asOf || series[series.length - 1].date;
  const floor = windowFloor(range, stamp);
  const ai = anchorIndex(series, floor);
  // If the anchor point itself falls AFTER the floor, the window opens BEFORE the series
  // begins (inception is inside the window), so the 0% baseline is the pre-inception state
  // (cum=0), NOT series[ai].cumulative. Using the first point's own cumulative here would
  // rebase inception onto itself → 0%, hiding the since-inception return. That zeroed the
  // "Return" tile for any fresh account whose whole history sits inside the range — e.g. a
  // per_deploy curve with a single inception snapshot (2026-08-17: model account read 0.00%
  // when its true return was −0.31%). Anchors that DO sit on/before the floor keep the normal
  // prior-close baseline, so mature-account windows are unchanged.
  const inceptionInWindow = series[ai].date > floor;
  const base = inceptionInWindow ? 1 : (1 + (series[ai].cumulative || 0));
  const points = series.slice(ai).map((p) => ({
    date: p.date,
    value: (1 + (p.cumulative || 0)) / base - 1,
  }));
  const windowReturn = points.length ? points[points.length - 1].value : null;
  return { points, windowReturn, anchorDate: series[ai].date };
}

// Rebase to an arbitrary [startISO, endISO] window. Anchor = last close on/before
// startISO (0% baseline); points truncated to <= endISO. Empty bounds fall back to
// inception / today. Same shape as rebaseSeries.
export function rebaseCustom(series, startISO, endISO) {
  if (!Array.isArray(series) || series.length === 0) {
    return { points: [], windowReturn: null, anchorDate: null };
  }
  const start = startISO || '0000-01-01';
  const ai = anchorIndex(series, start);
  // Same inception-in-window rule as rebaseSeries: if the anchor falls after the window
  // start, the 0% baseline is the pre-inception state (cum=0), not the first point's own
  // cumulative — otherwise inception rebases onto itself and the return reads 0%.
  const inceptionInWindow = series[ai].date > start;
  const base = inceptionInWindow ? 1 : (1 + (series[ai].cumulative || 0));
  let points = series.slice(ai).map((p) => ({
    date: p.date,
    value: (1 + (p.cumulative || 0)) / base - 1,
  }));
  if (endISO) points = points.filter((p) => p.date <= endISO);
  const windowReturn = points.length ? points[points.length - 1].value : null;
  return { points, windowReturn, anchorDate: series[ai].date };
}

// Window-aware summary from a rebased [{date, value(cumret)}] path: total return,
// annualised, vol (ann.), Sharpe (rf=0), max drawdown. Computed from the daily
// returns implied by consecutive cumulative values (252 trading days/yr).
export function seriesStats(points) {
  const empty = { ret: points && points.length ? points[points.length - 1].value : null, ann: null, vol: null, sharpe: null, maxdd: null };
  if (!points || points.length < 2) return empty;
  const rets = [];
  for (let i = 1; i < points.length; i += 1) {
    rets.push((1 + points[i].value) / (1 + points[i - 1].value) - 1);
  }
  const n = rets.length;
  const mean = rets.reduce((s, x) => s + x, 0) / n;
  const varc = rets.reduce((s, x) => s + (x - mean) * (x - mean), 0) / (n > 1 ? n - 1 : 1);
  const vol = Math.sqrt(varc) * Math.sqrt(252);
  const ret = points[points.length - 1].value;
  const years = n / 252;
  const ann = years > 0 ? (1 + ret) ** (1 / years) - 1 : null;
  const sharpe = vol > 0 && ann != null ? ann / vol : null;
  let peak = -Infinity, mdd = 0;
  points.forEach((p) => { const v = 1 + p.value; if (v > peak) peak = v; const dd = v / peak - 1; if (dd < mdd) mdd = dd; });
  return { ret, ann, vol, sharpe, maxdd: mdd };
}
